import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Clock, Sunrise, ArrowRight, Flame, XCircle, CheckCircle2, Pencil, Sparkles } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function RoutinePage() {
  const [routine, setRoutine] = useState(null);
  const [identityShifts, setIdentityShifts] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';

  useEffect(() => {
    Promise.all([
      api.get('/routine/current'),
      api.get('/identity-shifts'),
    ])
      .then(([routineRes, identityRes]) => {
        setRoutine(routineRes.data.routine);
        setIdentityShifts(identityRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-[--hc-text-muted] text-sm">Loading routine...</div>
      </div>
    );
  }

  if (!routine) {
    return (
      <div className="text-center py-20">
        <p className="text-[--hc-text-muted]">No routine found. Complete onboarding first.</p>
      </div>
    );
  }

  const newHabits = (routine.items || []).filter(i => i.section === 'before');
  const oldHabits = (routine.items || []).filter(i => i.section === 'after');
  const hasOldIdentity = identityShifts?.old_habits?.length > 0;
  const hasNewIdentity = identityShifts?.new_habits?.length > 0;
  const hasIdentityShifts = hasOldIdentity || hasNewIdentity;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[--hc-text]">My Routine</h2>
          <p className="text-sm text-[--hc-text-muted]">Your daily operating blueprint</p>
        </div>
        <button
          onClick={() => navigate(`/settings${isEmbed ? '?embed=true' : ''}`)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[--hc-border] text-xs font-medium text-[--hc-text-muted] hover:bg-[--hc-surface] hover:text-[--hc-text] transition-colors"
          data-testid="routine-edit-button"
        >
          <Pencil size={12} /> Edit Routine
        </button>
      </div>

      {/* Times */}
      <div className="flex gap-3">
        <div className="flex-1 rounded-xl border border-[--hc-border] bg-white px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[--hc-accent]/10 flex items-center justify-center">
            <Sunrise size={16} className="text-[--hc-accent]" />
          </div>
          <div>
            <p className="text-xs text-[--hc-text-muted]">Wake Up</p>
            <p className="text-lg font-bold text-[--hc-primary] tabular-nums">{routine.wake_time || '06:00'}</p>
          </div>
        </div>
        <div className="flex items-center text-[--hc-text-muted]">
          <ArrowRight size={16} />
        </div>
        <div className="flex-1 rounded-xl border border-[--hc-border] bg-white px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[--hc-primary]/10 flex items-center justify-center">
            <Flame size={16} className="text-[--hc-primary]" />
          </div>
          <div>
            <p className="text-xs text-[--hc-text-muted]">Start Operating</p>
            <p className="text-lg font-bold text-[--hc-primary] tabular-nums">{routine.start_operating_time || '08:00'}</p>
          </div>
        </div>
      </div>

      {/* Identity Shift Section */}
      {hasIdentityShifts && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="routine-identity-shifts">
          {/* New Identity */}
          {hasNewIdentity && (
            <div className="rounded-xl border-2 border-[--hc-accent]/25 bg-[--hc-accent-soft]/20 overflow-hidden" data-testid="routine-new-identity-card">
              <div className="px-4 py-2.5 border-b border-[--hc-accent]/15 bg-[--hc-accent]/8 flex items-center gap-2">
                <Sparkles size={14} className="text-[--hc-accent]" />
                <h3 className="text-xs font-semibold text-[--hc-primary] uppercase tracking-wide">Who I'm Becoming</h3>
              </div>
              <div className="px-4 py-3 space-y-2">
                {identityShifts.new_habits.map((habit, idx) => (
                  <p key={idx} className="text-sm text-[--hc-text] font-medium leading-relaxed">{habit}</p>
                ))}
              </div>
            </div>
          )}

          {/* Old Identity */}
          {hasOldIdentity && (
            <div className="rounded-xl border border-[--hc-border] bg-white overflow-hidden" data-testid="routine-old-identity-card">
              <div className="px-4 py-2.5 border-b border-[--hc-border] bg-[--hc-surface]/50 flex items-center gap-2">
                <XCircle size={14} className="text-[--hc-danger]/60" />
                <h3 className="text-xs font-semibold text-[--hc-text-muted] uppercase tracking-wide">Leaving Behind</h3>
              </div>
              <div className="px-4 py-3 space-y-2">
                {identityShifts.old_habits.map((habit, idx) => (
                  <p key={idx} className="text-xs text-[--hc-text-muted] line-through leading-relaxed">{habit}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Identity / Morning Routine Items */}
      <div className="rounded-xl border-2 border-[--hc-accent]/30 bg-[--hc-accent-soft]/30 overflow-hidden" data-testid="new-habits-section">
        <div className="px-5 py-3 bg-[--hc-accent]/10 border-b border-[--hc-accent]/20">
          <h3 className="text-sm font-bold text-[--hc-primary] flex items-center gap-2">
            <CheckCircle2 size={16} className="text-[--hc-accent]" />
            Morning Routine — Your Operating Blueprint
          </h3>
          <p className="text-xs text-[--hc-text-muted] mt-0.5">Your optimized morning routine. These actions build momentum and get you operating.</p>
        </div>
        <div className="divide-y divide-[--hc-accent]/10">
          {newHabits.map((item, idx) => (
            <div key={item.id || idx} className="flex items-center gap-3 px-5 py-3">
              <span className="w-6 h-6 rounded-lg bg-white border border-[--hc-accent]/20 flex items-center justify-center text-xs font-bold text-[--hc-primary] tabular-nums flex-shrink-0">
                {idx + 1}
              </span>
              <span className="flex-1 text-sm text-[--hc-text] font-medium">{item.text}</span>
              <span className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                item.grade === '+' ? 'bg-[--hc-success-soft] text-[--hc-success]' :
                item.grade === '/' ? 'bg-[--hc-surface-2] text-[--hc-text-muted]' :
                'bg-[--hc-danger-soft] text-[--hc-danger]'
              }`}>
                {item.grade}
              </span>
            </div>
          ))}
          {newHabits.length === 0 && (
            <div className="px-5 py-6 text-center text-sm text-[--hc-text-muted]">No routine items yet.</div>
          )}
        </div>
      </div>

      {/* Old Routine Items (moved to after operating time) */}
      {oldHabits.length > 0 && (
        <div className="rounded-xl border border-[--hc-border] bg-white overflow-hidden" data-testid="old-habits-section">
          <div className="px-5 py-3 bg-[--hc-surface] border-b border-[--hc-border]">
            <h3 className="text-sm font-bold text-[--hc-text-muted] flex items-center gap-2">
              <XCircle size={16} className="text-[--hc-danger]/60" />
              After Operating Time
            </h3>
            <p className="text-xs text-[--hc-text-muted] mt-0.5">Actions moved out of your morning routine. They happen after you start operating.</p>
          </div>
          <div className="divide-y divide-[--hc-border]">
            {oldHabits.map((item, idx) => (
              <div key={item.id || idx} className="flex items-center gap-3 px-5 py-2.5 opacity-60">
                <span className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                  item.grade === '-' ? 'bg-[--hc-danger-soft] text-[--hc-danger]' :
                  'bg-[--hc-surface-2] text-[--hc-text-muted]'
                }`}>
                  {item.grade}
                </span>
                <span className="flex-1 text-sm text-[--hc-text-muted] line-through">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Motivational note */}
      <div className="rounded-xl bg-[--hc-surface] border border-[--hc-border] px-5 py-3">
        <p className="text-xs text-[--hc-text-muted] leading-relaxed italic">
          Every morning you follow this routine, you reinforce who you're becoming. The old habits lose power. The new ones compound.
        </p>
      </div>
    </div>
  );
}
